import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import usePlaySoundHook from "./usePlaySoundHook";
import useUserSettingsHook from "./useUserSettingsHook";
import {
  DEFAULT_ALARM_VOLUME,
  DEFAULT_EXTRA_ALARM_MINUTE_DELAY,
} from "../utils/constant";
import { useAtomValue } from "jotai";
import { canSoundAtom } from "../atoms";
import { AlarmFileSounds, OrderStatus } from "../utils/enum";
import { OrdersLive } from "../v2/queries/interfacesV2";

const useSoundHook = (
  data: OrdersLive,
  play: boolean,
  setPlay: Dispatch<SetStateAction<boolean>>
) => {
  const canSound = useAtomValue(canSoundAtom);
  const { query } = useUserSettingsHook();
  const {
    alarm_volume = DEFAULT_ALARM_VOLUME,
    extra_alarm_minute_delay = DEFAULT_EXTRA_ALARM_MINUTE_DELAY,
    play_sound,
    play_sound_times,
    is_order_accepted_automatically,
    alarm_sound_file_choice = query?.data?.alarm_sound_file_choice ?? 1,
    extra_alarm_sound_times = query?.data?.extra_alarm_sound_times ?? 3,
  } = query?.data || {};
  const { playSound } = usePlaySoundHook(alarm_volume);
  const mainAlarmInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const alarmInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const soundInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (
      is_order_accepted_automatically === false &&
      extra_alarm_minute_delay > 0 &&
      play_sound
    ) {
      let mainAlarmCount = 0;
      const hasArrivedOrder = data?.[0]?.some(
        (o) => o?.order_information?.order_status === OrderStatus.ARRIVED
      );

      if (hasArrivedOrder) {
        mainAlarmInterval.current = setInterval(() => {
          let count = 0;
          mainAlarmCount++;
          if (mainAlarmCount >= extra_alarm_minute_delay * 2) {
            alarmInterval.current = setInterval(() => {
              count++;
              playSound(AlarmFileSounds.BEEP);
              if (count === extra_alarm_sound_times) {
                clearInterval(alarmInterval.current);
              }
            }, 2000);
          }
        }, 30000);
      }
    }

    return () => {
      clearInterval(alarmInterval.current);
      clearInterval(mainAlarmInterval.current);
    };
  }, [
    play,
    data,
    is_order_accepted_automatically,
    alarm_volume,
    extra_alarm_minute_delay,
    play_sound,
    extra_alarm_sound_times,
    playSound,
  ]);

  useEffect(() => {
    if (play && play_sound && canSound) {
      let count = 1;
      playSound(alarm_sound_file_choice);
      if (play_sound_times === 1) return;

      soundInterval.current = setInterval(() => {
        count++;
        playSound(alarm_sound_file_choice);
        if (count === play_sound_times && play_sound_times !== 0) {
          clearInterval(soundInterval.current);
          setPlay(false);
        }
      }, 2000);
    }
    return () => {
      clearInterval(soundInterval.current);
    };
  }, [play, play_sound, play_sound_times, canSound, alarm_sound_file_choice]);
};

export default useSoundHook;
